import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  DocumentData, 
  QueryDocumentSnapshot,
  Query,
  limit 
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { Subject } from '../types/subject';

const convertSubject = (doc: QueryDocumentSnapshot<DocumentData>): Subject => {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.title,
    type: data.type,
    chapter: data.chapter,
    year: data.year,
    session: data.session,
    location: data.location,
    pdfUrl: data.pdfUrl,
    videoUrl: data.videoUrl,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
};

// Mock data for development when Firestore is not available
const mockSubjects: Subject[] = [
  {
    id: '1',
    title: 'Dissertation sur la croissance économique',
    type: 'dissertation',
    chapter: 'croissance',
    year: '2023',
    session: 'normale',
    location: 'metropole',
    pdfUrl: 'https://firebasestorage.googleapis.com/v0/b/site-comprendre-les-ses-c5b6a.appspot.com/o/sujets%2F2023%2Fdissertation-croissance.pdf?alt=media',
    videoUrl: 'https://youtube.com/watch?v=example1',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: '2',
    title: 'EC1 - Le commerce international',
    type: 'ec1',
    chapter: 'commerce',
    year: '2023',
    session: 'normale',
    location: 'metropole',
    pdfUrl: 'https://firebasestorage.googleapis.com/v0/b/site-comprendre-les-ses-c5b6a.appspot.com/o/sujets%2F2023%2Fec1-commerce.pdf?alt=media',
    videoUrl: 'https://youtube.com/watch?v=example2',
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

export const getSubjects = async (filters: {
  type?: string;
  chapter?: string;
  year?: string;
  session?: string;
  location?: string;
  searchQuery?: string;
}): Promise<Subject[]> => {
  try {
    let q: Query = collection(db, 'subjects');

    // Add filters
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }
    if (filters.chapter) {
      q = query(q, where('chapter', '==', filters.chapter));
    }
    if (filters.year) {
      q = query(q, where('year', '==', filters.year));
    }
    if (filters.session) {
      q = query(q, where('session', '==', filters.session));
    }
    if (filters.location) {
      q = query(q, where('location', '==', filters.location));
    }

    // Add ordering and limit
    q = query(q, orderBy('createdAt', 'desc'), limit(50));

    const querySnapshot = await getDocs(q);
    let subjects = querySnapshot.docs.map(convertSubject);

    // Apply text search filter if provided
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase();
      subjects = subjects.filter(subject => 
        subject.title.toLowerCase().includes(searchLower) ||
        subject.chapter.toLowerCase().includes(searchLower)
      );
    }

    return subjects;
  } catch (error: any) {
    console.warn('Error fetching subjects from Firestore, using mock data:', error);
    
    // Return filtered mock data in development
    if (import.meta.env.DEV) {
      let filteredSubjects = [...mockSubjects];

      if (filters.type) {
        filteredSubjects = filteredSubjects.filter(s => s.type === filters.type);
      }
      if (filters.chapter) {
        filteredSubjects = filteredSubjects.filter(s => s.chapter === filters.chapter);
      }
      if (filters.year) {
        filteredSubjects = filteredSubjects.filter(s => s.year === filters.year);
      }
      if (filters.session) {
        filteredSubjects = filteredSubjects.filter(s => s.session === filters.session);
      }
      if (filters.location) {
        filteredSubjects = filteredSubjects.filter(s => s.location === filters.location);
      }
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        filteredSubjects = filteredSubjects.filter(subject => 
          subject.title.toLowerCase().includes(searchLower) ||
          subject.chapter.toLowerCase().includes(searchLower)
        );
      }

      return filteredSubjects;
    }

    throw new Error('Une erreur est survenue lors du chargement des sujets. Veuillez réessayer plus tard.');
  }
};

export const getPdfDownloadUrl = async (pdfPath: string): Promise<string> => {
  if (!pdfPath) {
    throw new Error('Le chemin du PDF est vide');
  }

  try {
    // If it's already a full URL, return it
    if (pdfPath.startsWith('http')) {
      return pdfPath;
    }

    // Clean and normalize the path
    const normalizedPath = pdfPath
      .split('/')
      .map(part => part.trim())
      .filter(Boolean)
      .join('/');

    // Try different path combinations
    const pathsToTry = [
      normalizedPath,
      `sujets/${normalizedPath}`,
      `bac/${normalizedPath}`,
      `pdf/${normalizedPath}`
    ];

    // Try each path until one works
    for (const path of pathsToTry) {
      try {
        const fileRef = ref(storage, path);
        const url = await getDownloadURL(fileRef);
        return url;
      } catch (error) {
        console.warn(`Failed to get download URL for path: ${path}`);
        continue;
      }
    }

    // If all paths fail, throw an error
    throw new Error('Fichier non trouvé dans le stockage');
  } catch (error) {
    console.error('Error getting PDF download URL:', error);
    throw new Error('Erreur lors du téléchargement du PDF. Veuillez vérifier que le fichier existe.');
  }
};

// Fonction pour télécharger un PDF de manière compatible avec Safari
export const downloadPdf = async (pdfPath: string, fileName?: string): Promise<void> => {
  try {
    // Obtenir l'URL du PDF
    const url = await getPdfDownloadUrl(pdfPath);
    
    // Déterminer le nom du fichier
    const defaultFileName = pdfPath.split('/').pop() || 'sujet.pdf';
    const finalFileName = fileName || defaultFileName;
    
    // Créer un élément a temporaire pour le téléchargement
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', finalFileName);
    link.setAttribute('target', '_blank');
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Simuler un clic sur le lien
    link.click();
    
    // Pour Safari, on ajoute un délai avant de supprimer le lien
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    // Méthode alternative pour Safari
    if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
      window.location.href = url;
    }
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};